import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Chip } from '@swyftx/aviary/atoms/Chip';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Heading } from '@swyftx/aviary/atoms/Typography';
import { Gift } from '@swyftx/aviary/icons/outlined';
import { types } from '@swyftx/marketing-utils';

import AssetIcon from '@global-components/AssetIcon/AssetIcon';

import { assetService } from '@shared/services';
import { formatCurrency } from '@shared/utils';

interface HeaderProps {
  reward: types.UserAssetReward;
}

export default function Header({ reward }: HeaderProps) {
  const { t } = useTranslation('rewards', { keyPrefix: 'rewardCard' });

  const rewardLabel = useMemo(() => {
    if (!reward) return null;

    const { rewardAssetId, valueAssetId, hasPayout, valueAmount } = reward;
    const isEntryDraw = !hasPayout || !valueAssetId || !rewardAssetId || !valueAmount;

    if (isEntryDraw) {
      return (
        <>
          <Gift className='h-14 w-14 text-inherit' />
          <Body size='small'>{t('entryIntoDraw')}</Body>
        </>
      );
    }

    const valueAsset = assetService.getAsset(valueAssetId);
    const rewardAsset = assetService.getAsset(rewardAssetId);
    const suffix = rewardAssetId === valueAssetId ? '' : `of ${rewardAsset?.code}`;

    return (
      <>
        <AssetIcon asset={rewardAsset} size={14} />
        <Body size='small'>
          {formatCurrency(valueAmount, valueAsset, { appendCode: true })} {suffix}
        </Body>
      </>
    );
  }, [reward]);

  return (
    <div className='flex flex-col items-start justify-start gap-16 md:flex-row md:items-center'>
      <Heading size='h3' weight='light'>
        {reward?.campaignName || ''}
      </Heading>
      {rewardLabel && (
        <Chip size='md' variant='subtle' color='secondary'>
          <FlexLayout className='py-2' alignItems='center' spacing={4}>
            {rewardLabel}
          </FlexLayout>
        </Chip>
      )}
    </div>
  );
}
